import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Wallet, Landmark, Calendar, Printer, Check } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";

// Formatea fecha "hoy" como YYYY-MM-DD para la conciliación por defecto
function _today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Cuentas() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [data, setData] = useState(null);
  const [modo, setModo] = useState("actual"); // actual | historial
  const [cierres, setCierres] = useState([]);
  const [cierreId, setCierreId] = useState("");

  useEffect(() => {
    api.get("/cierres-mes").then(({ data }) => setCierres(data || [])).catch(() => {});
  }, []);

  const load = async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (modo === "historial") {
        if (cierreId) params.cierre_id = cierreId;
        else params.historial = true;
      }
      const { data } = await api.get("/reports/cuentas", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [desde, hasta, modo, cierreId]);

  if (!data) return <div className="text-sm text-zinc-500">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="cuentas-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Finanzas</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Wallet className="w-8 h-8 text-[#10B981]" /> Libro de Cuentas
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Unifica ingresos (ventas) y egresos (gastos) por método de pago y por banco.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex gap-1">
            <button onClick={() => { setModo("actual"); setCierreId(""); }} className={`px-3 py-2 text-xs uppercase tracking-widest font-black rounded ${modo === "actual" ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="cuentas-tab-actual">Actual</button>
            <button onClick={() => setModo("historial")} className={`px-3 py-2 text-xs uppercase tracking-widest font-black rounded ${modo === "historial" ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="cuentas-tab-historial">Movimientos Archivados</button>
          </div>
          {modo === "historial" && (
            <select value={cierreId} onChange={(e) => setCierreId(e.target.value)} className="h-10 border border-zinc-200 rounded-md px-3 text-sm" data-testid="cuentas-hist-cierre">
              <option value="">Todos los cerrados</option>
              {cierres.map((c) => (
                <option key={c.id} value={c.id}>#{c.numero_cierre} · {c.mes_key} · {c.desde} → {c.hasta}</option>
              ))}
            </select>
          )}
          <DateRangeFilter desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} testidPrefix="cuentas" />
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-5 border-emerald-200 border-2 rounded-md bg-emerald-50/40">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Total Ingresos</div>
          <div className="font-manrope font-black text-4xl mt-1 text-emerald-700" data-testid="cuentas-ingresos">{formatMoney(data.total_ingresos)}</div>
          {data.anticipos_retenidos > 0 && <div className="text-[11px] text-zinc-600 mt-1" data-testid="cuentas-retenidos">Incluye {formatMoney(data.anticipos_retenidos)} de anticipos retenidos (pedidos cancelados)</div>}
        </Card>
        <Card className="p-5 border-red-200 border-2 rounded-md bg-red-50/40">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Total Egresos</div>
          <div className="font-manrope font-black text-4xl mt-1 text-red-700" data-testid="cuentas-egresos">{formatMoney(data.total_egresos)}</div>
        </Card>
        <Card className={`p-5 border-2 rounded-md ${data.saldo >= 0 ? "border-blue-200 bg-blue-50/40" : "border-red-300 bg-red-100"}`}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1"><Wallet className="w-3 h-3" /> Saldo</div>
          <div className={`font-manrope font-black text-4xl mt-1 ${data.saldo >= 0 ? "text-blue-700" : "text-red-800"}`} data-testid="cuentas-saldo">{formatMoney(data.saldo)}</div>
        </Card>
      </div>

      {/* Por método de pago */}
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="text-xl font-semibold mb-2">Por Método de Pago</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-left">
              <tr>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Método</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Ingresos</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Egresos</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Neto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.por_metodo.map((m) => (
                <tr key={m.metodo} className="hover:bg-zinc-50" data-testid={`cuentas-metodo-${m.metodo}`}>
                  <td className="px-4 py-2 font-semibold">{m.metodo}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{formatMoney(m.ingresos)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-red-700">{formatMoney(m.egresos)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-bold ${m.neto >= 0 ? "text-blue-700" : "text-red-700"}`}>{formatMoney(m.neto)}</td>
                </tr>
              ))}
              {data.por_metodo.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-400 py-4">Sin datos</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Por banco */}
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xl font-semibold flex items-center gap-2"><Landmark className="w-4 h-4" /> Por Banco</div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const params = new URLSearchParams();
                if (desde) params.set("desde", desde);
                if (hasta) params.set("hasta", hasta);
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/cuentas/por-banco.pdf?${params.toString()}`;
                const token = localStorage.getItem("token");
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `libro_cuentas_por_banco_${hasta || "hoy"}.pdf`;
                a.click();
              } catch (e) { toast.error("No se pudo generar el PDF"); }
            }}
            data-testid="cuentas-banco-pdf-btn"
          >
            <Printer className="w-3 h-3 mr-1" /> Imprimir PDF
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-left">
              <tr>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold">Banco</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">Ingresos</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">Egresos</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">Disponible</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold w-56">Notas</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-right">Diferencia</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.por_banco.map((b) => (
                <BancoRow
                  key={b.banco}
                  banco={b}
                  readOnly={modo === "historial"}
                />
              ))}
              {data.por_banco.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 py-4">Sin datos</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Cierre día a día */}
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xl font-semibold flex items-center gap-2"><Calendar className="w-4 h-4" /> Cierre Día a Día</div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const params = new URLSearchParams();
                if (desde) params.set("desde", desde);
                if (hasta) params.set("hasta", hasta);
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/cuentas/cierre-dia.pdf?${params.toString()}`;
                const token = localStorage.getItem("token");
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `libro_cuentas_cierre_dia_${hasta || "hoy"}.pdf`;
                a.click();
              } catch (e) { toast.error("No se pudo generar el PDF"); }
            }}
            data-testid="cuentas-cierre-pdf-btn"
          >
            <Printer className="w-3 h-3 mr-1" /> Imprimir PDF
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-left">
              <tr>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Fecha</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Ingresos</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Egresos</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Neto del día</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.cierre_dia.map((d) => (
                <tr key={d.fecha} className="hover:bg-zinc-50" data-testid={`cuentas-dia-${d.fecha}`}>
                  <td className="px-4 py-2 font-mono text-xs">{d.fecha}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{formatMoney(d.ingresos)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-red-700">{formatMoney(d.egresos)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-bold ${d.neto >= 0 ? "text-blue-700" : "text-red-700"}`}>{formatMoney(d.neto)}</td>
                </tr>
              ))}
              {data.cierre_dia.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-400 py-4">Sin movimientos en el rango</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Ronda 20 — Notas manuales por banco (independiente, no calcula nada).
// Se retiró el input de Conciliación (Ronda 18). Conservan Diferencia + Estado.
function BancoRow({ banco, readOnly = false }) {
  const [notas, setNotas] = useState(banco.notas || "");
  const [saving, setSaving] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);

  useEffect(() => { setNotas(banco.notas || ""); }, [banco.banco, banco.notas]);

  const disponible = Number(banco.disponible ?? banco.neto ?? ((banco.ingresos || 0) - (banco.egresos || 0)));
  const concNum = Number(banco.conciliacion || 0);
  const dif = Number((disponible - concNum).toFixed(2));
  const cuadrado = Math.abs(dif) < 0.01 && concNum > 0;

  const saveNotas = async () => {
    if (readOnly) return;
    if ((notas || "") === (banco.notas || "")) return; // sin cambios
    setSaving(true);
    try {
      await api.post("/banco-notas", { banco: banco.banco, notas });
      setSavedFlag(true);
      setTimeout(() => setSavedFlag(false), 1500);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <tr className="hover:bg-zinc-50" data-testid={`cuentas-banco-${banco.banco}`}>
      <td className="px-3 py-2 font-semibold">{banco.banco}</td>
      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatMoney(banco.ingresos)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-red-700">{formatMoney(banco.egresos)}</td>
      <td className={`px-3 py-2 text-right tabular-nums font-bold ${disponible >= 0 ? "text-blue-700" : "text-red-700"}`}>{formatMoney(disponible)}</td>
      <td className="px-2 py-1">
        {readOnly ? (
          <div className="text-xs text-zinc-700 whitespace-pre-wrap" data-testid={`notas-readonly-${banco.banco}`}>{banco.notas || "—"}</div>
        ) : (
          <div className="flex items-center gap-1">
            <Input
              type="text"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              onBlur={saveNotas}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
              className="h-8 text-sm"
              placeholder="Notas libres…"
              data-testid={`notas-input-${banco.banco}`}
              disabled={saving}
            />
            {savedFlag && <Check className="w-4 h-4 text-emerald-600" />}
          </div>
        )}
      </td>
      <td className={`px-3 py-2 text-right tabular-nums font-bold ${Math.abs(dif) < 0.01 ? "text-emerald-700" : "text-red-700"}`}>
        {formatMoney(dif)}
      </td>
      <td className="px-3 py-2 text-center">
        {concNum <= 0 ? (
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold" data-testid={`estado-${banco.banco}`}>Sin conciliar</span>
        ) : cuadrado ? (
          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded" data-testid={`estado-${banco.banco}`}>
            <Check className="w-3 h-3" /> Cuadrado
          </span>
        ) : (
          <span className="inline-block bg-red-100 text-red-800 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded" data-testid={`estado-${banco.banco}`}>
            Diferencia
          </span>
        )}
      </td>
    </tr>
  );
}
